import { defineStore } from "pinia"
import { store } from "../utils"
import type { QuestionItem, QuestionPageItem } from "@/api/teacher"

// 试题篮项目类型
export interface BasketItem extends QuestionPageItem {
  points: number // 试题分数
  point?: number // 兼容
  sort: number // 排序序号
}

export const usePaperStore = defineStore({
  id: "pure-paper",
  state: () => ({
    paperData: {
      radio: [],
      checkbox: [],
      judge: []
    },
    curBasket: [] as BasketItem[]
  }),
  getters: {
    /** @description paperData里所有数据的数量和 */
    paperTotalGetter() {
      return this.paperData.radio.length + this.paperData.checkbox.length + this.paperData.judge.length
    },
    /** @description 当前试题篮数量 */
    basketTotalGetter() {
      return this.curBasket.length
    },
    /** @description 试题篮总分（按 points/100 计算，缺省为0） */
    basketTotalScore() {
      return this.curBasket.reduce((total, item: any) => total + ((typeof item.points === "number" ? item.points : 0) / 100), 0)
    }
  },
  actions: {
    /** @description 添加试题 */
    addBasket(item: any) {
      const basketItem: BasketItem = {
        ...item,
        points: typeof (item as any).points === "number" ? (item as any).points : 0, // 默认0分（int）
        sort: this.curBasket.length + 1 // 自动排序
      }

      // 自动分配复合题分值（如果总分有值但小题无分）
      if (
        basketItem.qtTypeCat === 5 &&
        basketItem.points > 0 &&
        (basketItem as any).childrenDetail &&
        (basketItem as any).childrenDetail.length > 0
      ) {
        const children = (basketItem as any).childrenDetail
        const currentChildrenTotal = children.reduce((sum: number, c: any) => {
          return sum + (Number(c.points) || Number(c.point) || Number(c.score) || 0)
        }, 0)

        // 如果小题总分为0，则进行平均分配
        if (currentChildrenTotal === 0) {
          const totalPoints = basketItem.points
          const count = children.length
          const avg = Math.floor(totalPoints / count)
          const remainder = totalPoints % count

          children.forEach((child: any, index: number) => {
            child.points = avg + (index === count - 1 ? remainder : 0)
          })
        }
      }

      this.curBasket.push(basketItem)
    },
    /** @description 移除试题 */
    removeBasket(item: any) {
      if (!item) return (this.curBasket = [])

      const index = this.curBasket.findIndex((it) => it.id === item.id)
      if (index > -1) {
        this.curBasket.splice(index, 1)
        // 重新排序
        this.reorderBasket()
      }
    },
    /** @description 清空试题篮 */
    clearBasket() {
      this.curBasket = []
      // 🎯 关键修复：同时清除 localStorage 中的持久化数据
      try {
        localStorage.removeItem('pure-paper')
      } catch (error) {
        console.warn('清除 localStorage 失败:', error)
      }
    },
    /** @description 当前试题是否已被添加 */
    hasBasket(item: any) {
      return this.curBasket.some((it) => it.id === item.id)
    },
    /** @description 更新试题分数（前端传入显示分，内部保存为 points int） */
    updateQuestionScore(questionId: number, score: number) {
      const index = this.curBasket.findIndex((it) => it.id === questionId)
      if (index > -1) {
        const safeScore = Number.isFinite(score) ? score : 0
          ; (this.curBasket[index] as any).points = Math.round(safeScore * 100)
      }
    },
    /** @description 移动试题位置 */
    moveQuestion(questionId: number, direction: "up" | "down") {
      const index = this.curBasket.findIndex((it) => it.id === questionId)
      if (index === -1) return false

      if (direction === "up" && index > 0) {
        // 上移
        const temp = this.curBasket[index]
        this.curBasket[index] = this.curBasket[index - 1]
        this.curBasket[index - 1] = temp
        this.reorderBasket()
        return true
      } else if (direction === "down" && index < this.curBasket.length - 1) {
        // 下移
        const temp = this.curBasket[index]
        this.curBasket[index] = this.curBasket[index + 1]
        this.curBasket[index + 1] = temp
        this.reorderBasket()
        return true
      }

      return false
    },
    /** @description 重新排序试题篮 */
    reorderBasket() {
      this.curBasket.forEach((item, index) => {
        item.sort = index + 1
      })
    },
    /** @description 按小类获取试题列表 */
    getQuestionsByTypeId(qtTypeId: number) {
      return this.curBasket.filter((item) => item.qtTypeId === qtTypeId)
    }
  },
  persist: [
    {
      paths: ["curBasket"],
      storage: localStorage
    }
  ]
})

export function usePaperStoreHook() {
  return usePaperStore(store)
}
